/**
 * chartDefaults — initial-state helpers for chart selectors.
 *
 * Every Data Vault chart that has a well or analyte selector defaults to
 * "MW-1 + Benzene" if those are present in the data. If MW-1 isn't in
 * the well list (or Benzene isn't in the analyte list), the chart falls
 * back to "show everything" / "first item" so the chart isn't empty.
 */

interface WellLike {
  well_id: string;
  well_name: string;
}

interface AnalyteLike {
  compound: string;
}

interface WellWithAnalytes {
  analytes: Record<string, AnalyteLike>;
}

function findMw1<T extends WellLike>(wells: T[]): T | undefined {
  // Case-insensitive match — beta data sometimes capitalizes differently.
  return wells.find((w) => w.well_name.toUpperCase() === 'MW-1');
}

function findBenzeneCode(data: { wells: WellWithAnalytes[] }): string | undefined {
  for (const well of data.wells) {
    for (const [code, analyte] of Object.entries(well.analytes)) {
      if (analyte.compound.toLowerCase() === 'benzene') return code;
    }
  }
  return undefined;
}

function collectAnalyteCodes(data: { wells: WellWithAnalytes[] }): string[] {
  const seen = new Set<string>();
  for (const well of data.wells) {
    for (const code of Object.keys(well.analytes)) {
      seen.add(code);
    }
  }
  return Array.from(seen);
}

/**
 * Initial hiddenWellIds: hide every well except MW-1. If MW-1 isn't in
 * the dataset, return an empty Set (show everything).
 */
export function defaultHiddenWellIds(wells: WellLike[]): Set<string> {
  const mw1 = findMw1(wells);
  if (!mw1) return new Set();
  return new Set(wells.filter((w) => w.well_id !== mw1.well_id).map((w) => w.well_id));
}

/**
 * Initial hiddenAnalytes: hide every analyte except Benzene. If Benzene
 * isn't in the data, return an empty Set (show everything).
 */
export function defaultHiddenAnalytes(data: { wells: WellWithAnalytes[] }): Set<string> {
  const benzene = findBenzeneCode(data);
  if (!benzene) return new Set();
  return new Set(collectAnalyteCodes(data).filter((c) => c !== benzene));
}

/**
 * Default single-well selection: MW-1 if available, else the first well.
 */
export function defaultSelectedWellId(wells: WellLike[]): string {
  const mw1 = findMw1(wells);
  return (mw1 ?? wells[0])?.well_id ?? '';
}

/**
 * Default single-analyte selection: the Benzene code if available, else
 * the first analyte code in the data, else empty string.
 */
export function defaultSelectedAnalyteCode(data: { wells: WellWithAnalytes[] }): string {
  const benzene = findBenzeneCode(data);
  if (benzene) return benzene;
  const codes = collectAnalyteCodes(data);
  return codes[0] ?? '';
}

/**
 * Default Benzene COMPOUND NAME (not code). Used by DataExplorer where
 * axes are picked by display name, not by analyte code.
 */
export function defaultBenzeneCompoundName(data: { wells: WellWithAnalytes[] }): string | undefined {
  for (const well of data.wells) {
    for (const analyte of Object.values(well.analytes)) {
      if (analyte.compound.toLowerCase() === 'benzene') return analyte.compound;
    }
  }
  return undefined;
}
